<template>
  <div class="dashboard-container">
    <div v-if="selectedPage === null" class="dashboard-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <v-icon class="title-icon" size="32">mdi-view-dashboard</v-icon>
            Model Pages
          </h1>
          <p class="page-subtitle">Manage your lightning model pages</p>
        </div>
        <div class="header-actions">
          <CopyToClipboard
            label="embed code"
            :content-to-copy="`<script src='${cdnUrl}/lightning-model-page/cx/loader.js'><\/script><div class='lightning-model-page' data-account-id='${accountSettings.accountId}'></div>`"
          />
          <v-btn
            color="primary"
            size="large"
            variant="elevated"
            @click="showAddDialog = true"
            class="add-btn"
          >
            <v-icon start>mdi-plus</v-icon>
            <span class="d-none d-sm-inline">Add New Page</span>
            <span class="d-inline d-sm-none">Add</span>
          </v-btn>
        </div>
      </div>
    </div>

    <div v-if="selectedPage === null" class="table-container">
      <v-card elevation="2" class="table-card">
        <v-card-title class="table-title">
          <v-icon class="mr-2">mdi-table</v-icon>
          Pages Overview
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="tableHeaders"
            :items="tableData || []"
            :items-per-page="10"
            :loading="productSettings.loading"
            class="data-table"
            :item-class="() => 'table-row'"
            no-data-text="No model pages found. Click 'Add New Page' to create one."
            loading-text="Loading pages..."
          >
            <template v-slot:item="{ item }">
              <tr @click="handleRowClick(item)" class="table-row-clickable">
                <td class="model-cell">
                  <div class="model-info">
                    <v-icon size="20" class="model-icon">mdi-car</v-icon>
                    <span class="model-text">{{ item.model }}</span>
                  </div>
                </td>
                <td class="path-cell">
                  <code class="path-code">{{ item.path }}</code>
                </td>
              </tr>
            </template>
            <template v-slot:loading>
              <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>

    <div v-else class="selected-page-container">
    <v-card elevation="3" class="selected-page-card">
      <v-card-title class="selected-page-header">
        <v-btn
          icon
          variant="text"
          @click="handleBack"
          :disabled="productSettings.saving"
          class="back-btn"
        >
          <v-icon size="28">mdi-arrow-left</v-icon>
        </v-btn>
        <div class="page-title-section">
          <h2 class="selected-page-title">
            {{ `${selectedPage.year} ${selectedPage.make} ${selectedPage.model}` }}
          </h2>
          <div class="page-meta">
            <v-chip size="small" variant="outlined" color="primary">
              <v-icon start size="16">mdi-calendar</v-icon>
              {{ selectedPage.year }}
            </v-chip>
            <v-chip size="small" variant="outlined" color="secondary" class="ml-2">
              <v-icon start size="16">mdi-tag</v-icon>
              {{ selectedPage.make }}
            </v-chip>
          </div>
        </div>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="selected-page-content">
        <div class="actions-section">
          <div class="copy-section">
            <CopyToClipboard
              label="Path"
              :content-to-copy="`/models/${selectedPage.year}/${selectedPage.make}/${selectedPage.model}`"
            />
          </div>
          
          <div class="action-buttons">
            <v-btn
              variant="elevated"
              color="success"
              size="large"
              @click="previewModelPage"
              class="action-btn"
            >
              <v-icon start>mdi-eye</v-icon>
              <span class="d-none d-sm-inline">Preview</span>
              <span class="d-inline d-sm-none">View</span>
            </v-btn>
            <v-btn
              variant="elevated"
              color="primary"
              size="large"
              @click="viewInventory"
              :disabled="!accountSettings.websiteUrl"
              class="action-btn"
            >
              <v-icon start>mdi-car</v-icon>
              <span class="d-none d-sm-inline">View Inventory</span>
              <span class="d-inline d-sm-none">Inventory</span>
            </v-btn>
            <v-btn
              variant="elevated"
              color="error"
              size="large"
              @click="removePage(selectedPage)"
              :disabled="productSettings.saving"
              class="action-btn"
            >
              <v-icon start>mdi-delete</v-icon>
              <span class="d-none d-sm-inline">Delete</span>
              <span class="d-inline d-sm-none">Del</span>
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
    </div>
  </div>

  <AddNewModelDialog v-model="showAddDialog" @add-new-page="addNewPage" />

  <v-dialog v-model="showDeleteDialog" max-width="500px" persistent>
    <v-card>
      <v-card-title class="dialog-title">
        <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
        Delete Page
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="dialog-content">
        <p class="dialog-message">
          Are you sure you want to delete this page? This action cannot be undone.
        </p>
      </v-card-text>
      <v-card-actions class="dialog-actions">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="cancelDelete" class="mr-2"> Cancel </v-btn>
        <v-btn color="error" variant="elevated" @click="deleteSelectedPage">
          <v-icon start>mdi-delete</v-icon>
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="showConfirmDialog" max-width="500px" persistent>
    <v-card>
      <v-card-title class="dialog-title">
        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
        Unsaved Changes
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="dialog-content">
        <p class="dialog-message">
          You have unsaved changes. Are you sure you want to discard them?
        </p>
      </v-card-text>
      <v-card-actions class="dialog-actions">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showConfirmDialog = false" class="mr-2">
          Keep Editing
        </v-btn>
        <v-btn color="error" variant="elevated" @click="confirmDiscard">
          Discard Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-snackbar v-model="showSnackbar" :timeout="2000" :color="snackbarColor">
    {{ snackbarText }}

    <template #actions>
      <v-btn icon="mdi-close" @click="showSnackbar = false"></v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import AddNewModelDialog from '@/components/AddNewModelDialog.vue'
import CopyToClipboard from '@/components/CopyToClipboard.vue'
import type { IPage, IProductSettings } from '@/types'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'

const { cdnUrl } = useJazelAppConfig()

const tableHeaders = [
  {
    title: 'Model',
    key: 'model',
    align: 'start' as const,
    value: 'model',
  },
  {
    title: 'Path',
    key: 'path',
    align: 'start' as const,
    value: 'path',
  },
]

const accountSettings = useAccountSettingsStore()
const productSettings = useProductSettingsStore()

const pages = computed(() => {
  return productSettings.settings?.pages ?? []
})
const filteredPages = ref<IPage[]>([])
const pageToDelete = ref<IPage | null>(null)
const selectedPage = ref<IPage | null>(null)
const pagesCount = ref(0)

type TableDataItem = {
  model: string
  path: string
}

const tableData = computed(() => {
  return filteredPages.value.map((page) => ({
    model: page.model,
    path: page.path,
  }))
})

const showAddDialog = ref(false)
const showDeleteDialog = ref(false)

const showSnackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const addNewPage = (newPage: IPage) => {
  if (productSettings.settings === null) {
    return
  }

  const newSettings: IProductSettings = {
    ...productSettings.settings,
    pages: productSettings.settings?.pages
      ? [...productSettings.settings?.pages, newPage]
      : [newPage],
  }

  productSettings
    .saveSettings(newSettings)
    .then(() => {
      showSnackbar.value = true
      snackbarText.value = 'New page added successfully'
      snackbarColor.value = 'success'

      showAddDialog.value = false

      pagesCount.value = productSettings.settings?.pages.length ?? 0
      filteredPages.value = [...filteredPages.value, newPage]
    })
    .catch((err) => {
      console.error(err)
      showSnackbar.value = true
      snackbarText.value = 'Failed to add new page'
      snackbarColor.value = 'error'
    })
}

const originalPage = ref<IPage | null>(null)
const pendingAction = ref<'back' | 'cancel' | null>(null)
const showConfirmDialog = ref<boolean>(false)

const hasChanges = computed(() => {
  if (!selectedPage.value || !originalPage.value) return false

  return JSON.stringify(selectedPage.value) !== JSON.stringify(originalPage.value)
})

const handleBack = () => {
  if (hasChanges.value) {
    pendingAction.value = 'back'
    showConfirmDialog.value = true
  } else {
    selectedPage.value = null
  }
}

const confirmDiscard = () => {
  showConfirmDialog.value = false
  pendingAction.value = null
  selectedPage.value = null
}

const previewModelPage = () => {
  if (!selectedPage.value || !accountSettings.accountId) return

  const modelPageUrl = `${import.meta.env.VITE_CX_URL}research/models/${selectedPage.value.year}/${selectedPage.value.make}/${selectedPage.value.model}?account_id=${accountSettings.accountId}`

  window.open(modelPageUrl, '_blank')
}

const viewInventory = () => {
  if (!accountSettings.websiteUrl) return
  window.open(accountSettings.websiteUrl, '_blank')
}

const removePage = (item: IPage | TableDataItem) => {
  pageToDelete.value = pages.value.find((page) => page.path === item.path) ?? null
  showDeleteDialog.value = true
}

const cancelDelete = () => {
  pageToDelete.value = null
  showDeleteDialog.value = false
}

const deleteSelectedPage = () => {
  if (pageToDelete.value === null) {
    return
  }

  if (!productSettings.settings?.globalSettings) {
    return
  }

  const newSettings: IProductSettings = {
    ...productSettings.settings,
    pages:
      productSettings.settings?.pages.filter((page) => page.path !== pageToDelete.value?.path) ??
      [],
  }

  productSettings
    .saveSettings(newSettings)
    .then(() => {
      showSnackbar.value = true
      snackbarText.value = 'Page deleted successfully'
      snackbarColor.value = 'success'

      showDeleteDialog.value = false
      selectedPage.value = null

      pagesCount.value = productSettings.settings?.pages.length ?? 0
      filteredPages.value = filteredPages.value.filter((p) => p.path !== pageToDelete.value?.path)
    })
    .catch((err) => {
      console.error(err)
      showSnackbar.value = true
      snackbarText.value = 'Failed to delete page'
      snackbarColor.value = 'error'
    })
}

const handleRowClick = (item: TableDataItem) => {
  console.log('pages', pages)
  const foundPage = pages.value.find((page) => page.path === item.path)
  selectedPage.value = foundPage ?? null
  originalPage.value = JSON.parse(JSON.stringify(selectedPage.value))
}

onMounted(async () => {
  console.log('Mounted Dashboard component', productSettings.settings)
  pagesCount.value = productSettings.settings?.pages.length ?? 0
  filteredPages.value = productSettings.settings?.pages ?? []
})

watch(selectedPage, (page: IPage | null) => {
  if (page) {
    console.log('Selected page:', page)

    if (!Array.isArray(page.highlightedFeatures)) {
      if (page.highlightedFeatures && typeof page.highlightedFeatures === 'object') {
        page.highlightedFeatures = [page.highlightedFeatures]
      } else {
        page.highlightedFeatures = []
      }
    }
  }
})
</script>

<style scoped>
.dashboard-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.dashboard-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 24px;
}

.title-section {
  flex: 1;
  min-width: 200px;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.title-icon {
  color: rgb(var(--v-theme-primary));
}

.page-subtitle {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.header-actions > * {
  display: flex;
  align-items: center;
}

.add-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
}

:deep(.header-actions .v-btn),
:deep(.action-buttons .v-btn) {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.table-container {
  margin-top: 24px;
}

.table-card {
  border-radius: 12px;
  overflow: hidden;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  padding: 20px 24px;
  display: flex;
  align-items: center;
}

.data-table {
  border-radius: 0;
}

.table-row-clickable {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.table-row-clickable:hover {
  background-color: rgba(var(--v-theme-primary), 0.08) !important;
}

.model-cell {
  font-weight: 500;
}

.model-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.model-icon {
  color: rgb(var(--v-theme-primary));
}

.model-text {
  font-size: 15px;
}

.path-cell {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.path-code {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.selected-page-container {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.selected-page-card {
  border-radius: 12px;
  overflow: hidden;
}

.selected-page-header {
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.1) 0%, rgba(var(--v-theme-surface), 1) 100%);
}

.back-btn {
  transition: transform 0.2s ease;
}

.back-btn:hover {
  transform: translateX(-4px);
}

.page-title-section {
  flex: 1;
}

.selected-page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.page-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-page-content {
  padding: 32px 24px;
}

.actions-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.copy-section {
  flex: 1;
  min-width: 200px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  min-width: 140px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  padding: 20px 24px;
}

.dialog-content {
  padding: 24px;
}

.dialog-message {
  font-size: 15px;
  line-height: 1.6;
  color: rgba(var(--v-theme-on-surface), 0.87);
  margin: 0;
}

.dialog-actions {
  padding: 16px 24px;
}

:deep(.v-data-table-header__content > span) {
  font-weight: 600 !important;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

:deep(.v-data-table__td) {
  padding: 16px;
}

:deep(.v-data-table__th) {
  padding: 16px;
  background-color: rgba(var(--v-theme-surface-variant), 0.3);
}

@media (max-width: 960px) {
  .dashboard-container,
  .selected-page-container {
    padding: 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .selected-page-title {
    font-size: 22px;
  }

  .actions-section {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons {
    width: 100%;
  }

  .action-btn {
    flex: 1;
    min-width: auto;
  }
}
</style>
